import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withFieldProps } from 'hooks/useFieldProps';
import { v1 as uuid } from 'uuid';
import InputMask from 'react-input-mask';
import FlyupLabel from 'common/FlyupLabel';
import AutocompleteMenu from 'common/AutocompleteMenu';
import Spinner from 'common/Spinner';
import { MASK_FORMAT } from 'config/ui';
import { withStyles } from 'theme/withStyles';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

import inputStyles from './styles/input';
import rootStyles from './styles/root';

const styles = ({ forms, palette, spacing, typography }) => ({
  input: inputStyles({ forms, palette, typography }),
  root: rootStyles({ spacing }),
});

const beforeMaskedValueChange = (subAppliedMask) => (newState, oldState, userInput) => {
  let { selection, value } = newState;
  let cursorPosition = selection ? selection.start : null;
  // keep minus if entered by user
  if (subAppliedMask && newState.value && !oldState.value) {
    cursorPosition += 1;
    selection = { end: cursorPosition, start: cursorPosition };
  }
  if (value.endsWith('-') && userInput !== '-' && !oldState.value.endsWith('-')) {
    if (cursorPosition === value.length) {
      cursorPosition -= 1;
      selection = { end: cursorPosition, start: cursorPosition };
    }
    value = value.slice(0, -1);
  }
  return {
    selection,
    value,
  };
};

const String = ({
  classes,
  error,
  fieldDomId,
  format,
  googlePlacesAutocomplete,
  hasFocus,
  help,
  inputmode,
  loading,
  name,
  onBlur,
  onChange,
  onFocus,
  onPaste,
  placeholder,
  readOnly,
  required,
  textTransform,
  tooltip,
  type,
  validate,
  value,
}) => {
  const inputNode = useRef();
  const fieldInput = inputNode && inputNode.current ? inputNode.current.value : '';
  const [keyUpEvent, setKeyUpEvent] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const inputPurpose = useMemo(() => {
    if (googlePlacesAutocomplete) {
      return uuid();
    }
    if (name.includes('firstName')) {
      return 'given-name';
    }
    if (name.includes('middleName')) {
      return 'additional-name';
    }
    if (name.includes('lastName')) {
      return 'family-name';
    }
    if (name.includes('addressLine1')) {
      return 'address-line1';
    }
    if (name.includes('addressLine2')) {
      return 'address-line2';
    }
    if (name.includes('city')) {
      return 'address-level2';
    }
    if (name.includes('Addr_state')) {
      return 'address-level1';
    }
    if (name.includes('postalCode')) {
      return 'postal-code';
    }
    if (name.includes('dateOfBirth')) {
      return 'bday';
    }
    if (name.includes('emailAddress')) {
      return 'email';
    }
    if (name.includes('homePhoneNumber') || name.includes('business_phoneNumber')) {
      return 'tel';
    }
    if (name.includes('nameOnCard')) {
      return 'cc-name';
    }
    if (name.includes('linkedPaymentCard_accountNumber')) {
      return 'cc-number';
    }
    if (name.includes('linkedPaymentCard_expiryDate')) {
      return 'cc-exp';
    }
    if (name.includes('linkedPaymentCard_cardSecurityCode')) {
      return 'cc-csc';
    }
    if (name.includes('employerName') || name.includes('business_name')) {
      return 'organization';
    }
    if (
      name.includes('employment_title') ||
      name.includes('role_title') ||
      name.includes('Employment_title')
    ) {
      return 'organization-title';
    }
    if (name.includes('country')) {
      return 'country-name';
    }
    return uuid();
  }, [googlePlacesAutocomplete, name]);
  const shouldApplySubMask =
    !['A', '0', '*'].includes(format.charAt(0)) &&
    (!value || value?.length === 0 || value === format[0]);
  const derivedFormat = shouldApplySubMask ? format.substring(1) : format;

  const applyTextTransform = (val) => {
    if (typeof val === 'string') {
      if (textTransform === 'uppercase') {
        return val.toUpperCase();
      }
      if (textTransform === 'lowercase') {
        return val.toLowerCase();
      }
      if (textTransform === 'capitalize') {
	    // Convert input to lowercase and then find the first character from each word and convert it to uppercase.
        return val.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, firstChar => firstChar.toUpperCase());
      }
    }

    return val;
  };

  // Make sure we don't leave any stray format characters in the data
  useEffect(() => {
    if (format[0] === value && !Object.keys(MASK_FORMAT).includes(value)) {
      onChange(name, '');
    }
  }, [format, name, onChange, value]);

  return (
    <>
      <div className={classnames('form-group', { error }, classes.root)}>
        <Spinner loading={loading} />
        <FlyupLabel
          fieldName={name}
          error={error}
          value={value}
          htmlFor={fieldDomId}
          required={required}
          tooltip={tooltip}
        />
        {format ? (
          <InputMask
            mask={derivedFormat}
            maskChar={null}
            value={value}
            formatChars={MASK_FORMAT}
            readOnly={readOnly}
            required={required}
            onChange={(e) => {
              const transformedValue = applyTextTransform(e.target.value);
              const isPhoneAndStartsWithNumber =
                transformedValue && format[0] === '(' && transformedValue[0] !== format[0];
              onChange(
                name,
                isPhoneAndStartsWithNumber ? format[0] + transformedValue : transformedValue,
              );
              if (error) {
                validate(false);
              }
            }}
            beforeMaskedValueChange={beforeMaskedValueChange(shouldApplySubMask)}
            onFocus={onFocus}
            onBlur={() => {
              onBlur();
              validate();
            }}
            onPaste={onPaste}
            autoComplete={inputPurpose}
          >
            {(inputProps) => (
              <input
                {...inputProps}
                type={type}
                className={classnames('form-control', classes.input)}
                name={name}
                id={fieldDomId}
                inputMode={inputmode}
                placeholder={placeholder}
                readOnly={readOnly}
                ref={inputNode}
                maxLength={
                  name.toLowerCase().includes('linkedpaymentcard_expirydate')
                    ? `${format.length}`
                    : undefined
                }
                aria-describedby={
                  classnames({
                    [`help-${name}`]: help,
                    [`error-${name}`]: error,
                  }) || undefined
                }
                aria-invalid={error}
              />
            )}
          </InputMask>
        ) : (
          <input
            type={type}
            className={classnames('form-control', classes.input)}
            name={name}
            id={fieldDomId}
            value={value}
            inputMode={inputmode}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            onChange={(e) => {
              const transformedValue = applyTextTransform(e.target.value);
              onChange(name, transformedValue);
              if (error) {
                validate(false);
              }
            }}
            onFocus={onFocus}
            onBlur={() => {
              onBlur();
              validate();
              if (googlePlacesAutocomplete) {
                setKeyUpEvent(`blur:${uuid()}`);
              }
            }}
            onPaste={onPaste}
            autoComplete={googlePlacesAutocomplete ? 'off' : inputPurpose}
            ref={inputNode}
            onKeyUp={googlePlacesAutocomplete ? (e) => setKeyUpEvent(`${e.key}:${uuid()}`) : null}
            aria-describedby={
              classnames({
                [`help-${name}`]: help,
                [`error-${name}`]: error,
                [`autocomplete-help-${name}`]: googlePlacesAutocomplete,
              }) || undefined
            }
            aria-activedescendant={
              googlePlacesAutocomplete && activeItemIndex !== -1
                ? `${name}-item${activeItemIndex}`
                : undefined
            }
            aria-invalid={error}
            aria-controls={googlePlacesAutocomplete ? `type-ahead-list${name}` : undefined}
            autoCapitalize={googlePlacesAutocomplete ? 'none' : undefined}
            aria-autocomplete={googlePlacesAutocomplete ? 'list' : undefined}
            role={googlePlacesAutocomplete ? 'combobox' : undefined}
            aria-expanded={googlePlacesAutocomplete ? hasFocus && !!fieldInput : undefined}
          />
        )}
        {googlePlacesAutocomplete && (
          <>
            <AutocompleteMenu
              clientId={name}
              fieldInput={fieldInput}
              keyUpEvent={keyUpEvent}
              hasFocus={hasFocus}
              setActiveItemIndex={setActiveItemIndex}
              activeItemIndex={activeItemIndex}
            />
            <div className="sr-only hidden" id={`autocomplete-help-${name}`}>
              Provides auto-suggestions when entering text
            </div>
          </>
        )}
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        <Help help={help} id={`help-${name}`} />
      </div>
      <ErrorMsg name={name} />
    </>
  );
};

String.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  googlePlacesAutocomplete: PropTypes.bool.isRequired,
  hasFocus: PropTypes.bool.isRequired,
  help: PropTypes.string.isRequired,
  inputmode: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  textTransform: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const StringStyled = withStyles(styles)(String);

const mapStateToProps = ({ fields, ui }, { name }) => {
  const field = fields[name];
  const getType = (fieldType) => {
    switch (fieldType) {
      case 'PASSWORD':
        return 'password';
      case 'LOCAL_DATE':
      default:
        return 'text';
    }
  };
  return {
    googlePlacesAutocomplete: field.fieldType === 'GOOGLE_PLACES_AUTOCOMPLETE',
    hasFocus: ui.fieldFocus === name,
    textTransform: field.textTransform ?? '',
    type: getType(field.fieldType),
  };
};

export default withFieldProps(mapStateToProps)(StringStyled);
